import { useQuery } from '@apollo/client';
import { CURRENT_USER, GOOGLE_OAUTH_QUERY } from '@repo-sapiens/graphql';
import {
  MicrosoftButton,
  NewGoogleButton,
  PageLayout,
  Separator,
  Text,
} from '../../../components';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GetMeResponse, GoogleOauthQueryDto } from '../../../types';
import { E_Route } from '@repo-sapiens/constants';
import { config } from '../../../config';
import { css, keyframes } from '@emotion/css';
import {
  CustomGoogleIcon,
  MicrosoftIcon,
  MultiplayerWhitePlainLogo,
  QuoteIcon,
} from '../../../components/icons';
import { authFlowInterface } from '../../settings/utils';
import { trackEvent } from '../../../libs';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  25% {
    transform: translateY(-10px) rotate(-2deg);
  }
  75% {
    transform: translateY(5px) rotate(2deg);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const drawLine = keyframes`
  from {
    height: 0;
  }
  to {
    height: 100%;
  }
`;

const drawLineHorizontal = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

const bounceIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
`;

export const OnboardingScreen: React.FC = () => {
  const { data: userData, refetch: refetchUser } = useQuery<GetMeResponse>(
    CURRENT_USER,
    {
      fetchPolicy: 'network-only',
      pollInterval: 1000,
    },
  );

  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState<'google' | 'outlook' | null>(
    null,
  );
  const [quoteVisible, setQuoteVisible] = useState(false);
  const [displayedQuote, setDisplayedQuote] = useState('');
  const fullQuote =
    'I appreciate the reduced clutter and the intuitive way in which it organizes my emails, mirroring my own preferences.';

  const { data: googleOauthData } = useQuery<
    {
      googleOAuthUrl: string;
    },
    GoogleOauthQueryDto
  >(GOOGLE_OAUTH_QUERY, {
    variables: {
      oauthData: {
        redirect: `${config.host}${E_Route.Done}`,
        type: 'onboarding',
        services: ['gmail', 'calendar'],
      },
      userId: userData?.me?._id || undefined,
    },
  });

  const startOauth = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    trackEvent('Onboarding - Google OAuth', {
      source: 'web',
    });
    // auth(authData?.gmailOAuthUrl!, e, 'gmail');
    authFlowInterface(googleOauthData?.googleOAuthUrl || '', e, () => {
      console.log('OAuth flow started');
      refetchUser().then().catch();
    });
  };

  // Typewriter effect for quote
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuoteVisible(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (quoteVisible) {
      let index = 0;
      const typewriterInterval = setInterval(() => {
        if (index <= fullQuote.length) {
          setDisplayedQuote(fullQuote.slice(0, index));
          index++;
        } else {
          clearInterval(typewriterInterval);
        }
      }, 20);

      return () => clearInterval(typewriterInterval);
    }
  }, [quoteVisible, fullQuote]);

  useEffect(() => {
    console.log('OnboardingScreen mounted', userData?.me?.partialAccount);
    if (
      typeof userData?.me?.partialAccount === 'boolean' &&
      !userData?.me?.partialAccount
    ) {
      // If the user has completed onboarding, redirect them to the choose subscription page
      navigate(E_Route.ChoosePlan);
    }
  }, [userData?.me?.partialAccount, navigate]);

  return (
    <PageLayout title="Onboarding | Multiplayer">
      {/* Animated background particles */}
      <div
        className={css`
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 0;
          overflow: hidden;

          &::before,
          &::after {
            content: '';
            position: absolute;
            width: 400px;
            height: 400px;
            border-radius: 50%;
            opacity: 0.03;
          }

          &::before {
            background: radial-gradient(circle, #ffffff, transparent);
            top: -200px;
            left: -200px;
            animation: ${float} 20s ease-in-out infinite;
          }

          &::after {
            background: radial-gradient(circle, #808080, transparent);
            bottom: -200px;
            right: -200px;
            animation: ${float} 25s ease-in-out infinite reverse;
          }
        `}
      />

      <div
        className={css`
          display: grid;
          grid-template-columns: 68% 32%;
          gap: 32px;
          padding: 0px 42px;
          align-items: center;
          position: relative;
          z-index: 1;
          @media (max-width: 1024px) {
            grid-template-columns: 1fr;
            padding: 12px;
          }

          @media (max-width: 768px) {
            grid-template-columns: 1fr;
            padding: 0px;
          }
        `}
      >
        <div
          className={css`
            width: 100%;
            padding-top: 64px;
          `}
        >
          <div
            className={css`
              width: 100%;
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: flex-start;
              margin-bottom: 32px;
              padding-inline: 24px;
              animation: ${fadeInUp} 0.8s ease-out;
              @media (max-width: 648px) {
                flex-direction: column;
                padding-inline: 0px;
              }
            `}
          >
            <div
              className={css`
                animation: ${float} 4s ease-in-out;
                margin-right: 16px;
                @media (max-width: 648px) {
                  margin-bottom: 16px;
                  margin-right: 0px;
                }
              `}
            >
              <MultiplayerWhitePlainLogo
                style={{ width: '72px', height: '72px' }}
              />
            </div>
            <div
              className={css`
                margin-top: -16px;
                animation: ${slideInFromLeft} 1s ease-out 0.3s both;
                @media (max-width: 648px) {
                  text-align: center;
                  max-width: 300px;
                }
              `}
            >
              <Text.H2
                className={css`
                  background: linear-gradient(90deg, #ffffff, #808080, #ffffff);
                  background-size: 200% 100%;
                  -webkit-background-clip: text;
                  background-clip: text;
                  animation: ${gradientShift} 3s ease-in-out infinite;
                `}
              >
                Connect your email
              </Text.H2>
              <Text.BodySM
                color="seconday"
                className={css`
                  animation: ${fadeIn} 1.2s ease-out 0.5s both;
                `}
              >
                Experience the freedom of a well-organized day
              </Text.BodySM>
            </div>
          </div>
          {/* Workspace Section */}
          <div
            className={css`
              display: flex;
              width: 100%;
              flex-direction: column;
              justify-content: center;
              margin-bottom: 32px;
              padding-inline: 24px;
              @media (max-width: 810px) {
                flex-direction: column;
              }
              @media (max-width: 648px) {
                padding-inline: 0px;
              }
            `}
          >
            <IntegrationItem
              service="Google"
              onClick={startOauth}
              isHovered={hoveredCard === 'google'}
              onHover={(hover) => setHoveredCard(hover ? 'google' : null)}
              animationDelay="0.5s"
            />
            <Separator.Horizontal spacing={2} />
            <IntegrationItem
              service="Outlook"
              onClick={() => {
                console.log('Outlook service selected');
              }}
              isHovered={hoveredCard === 'outlook'}
              onHover={(hover) => setHoveredCard(hover ? 'outlook' : null)}
              animationDelay="0.7s"
            />
          </div>
          {/* End of Workspace */}
        </div>

        {/* Quote Section */}
        <div
          className={css`
            height: 100%;
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 34px;
            position: relative;
            animation: ${slideInFromRight} 1s ease-out 0.9s both;
            @media (max-width: 510px) {
              padding: 12px;
            }
          `}
        >
          <div
            className={css`
              position: absolute;
              top: 0px;
              left: 0;
              width: 1px;
              height: 0;
              border-left: 1px solid #3a3a3a;
              animation: ${drawLine} 1s ease-out 1.2s forwards;
              @media (max-width: 1024px) {
                top: -60px;
                left: 0px;
                width: 0;
                height: 1px;
                border-left: none;
                border-top: 1px solid #3a3a3a;
                animation: ${drawLineHorizontal} 1s ease-out 1.2s forwards;
              }
            `}
          />
          <div
            className={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              max-width: 642px;
              @media (max-width: 510px) {
                width: 100%;
              }
            `}
          >
            <div
              className={css`
                position: relative;
                width: 100%;
                margin-bottom: 16px;
                @media (max-width: 510px) {
                  margin-top: 32px;
                }
              `}
            >
              <div
                className={css`
                  position: absolute;
                  top: -60px;
                  left: 0px;
                  animation: ${bounceIn} 0.8s ease-out 1.4s both;
                `}
              >
                <QuoteIcon
                  fill="highlight/1"
                  style={{ width: '47px', height: '32px' }}
                  className={css`
                    animation: ${pulse} 2s ease-in-out infinite;
                    animation-delay: 2.5s;
                  `}
                />
              </div>
              <Text.H5
                style={{
                  fontSize: '32px',
                  lineHeight: '40px',
                  minHeight: '120px',
                }}
                className={css`
                  @media (max-width: 510px) {
                    font-size: 24px;
                    line-height: 32px;
                    min-height: '96px';
                  }
                  &::after {
                    content: '|';
                    animation: ${fadeIn} 0.7s ease-in-out infinite alternate;
                    margin-left: 2px;
                    display: ${displayedQuote.length === fullQuote.length
                      ? 'none'
                      : 'inline'};
                  }
                `}
              >
                {displayedQuote}
              </Text.H5>
            </div>
            <Text.Body
              color="highlight/1"
              className={css`
                animation: ${fadeInUp} 0.8s ease-out 2.5s both;
              `}
            >
              Triston P.
              <br />
              Assistant Sales Manager
            </Text.Body>
          </div>
        </div>
        {/* Terms of Service */}
        <div
          className={css`
            width: 100%;
            display: none;
            @media (max-width: 810px) {
              margin-top: 24px;
              margin-bottom: 24px;
            }
          `}
        >
          <div
            className={css`
              width: fit-content;
              margin: auto;
            `}
          >
            <Text.BodyXS>
              By proceeding, you agree to the{' '}
              <Text.Link
                size="body-xs"
                target="_blank"
                rel="noreferrer"
                href="https://www.multiplayer.work/terms-of-service"
              >
                Terms of Service
              </Text.Link>{' '}
              and{' '}
              <Text.Link
                size="body-xs"
                target="_blank"
                rel="noreferrer"
                href="https://www.multiplayer.work/privacy-policy"
              >
                Privacy Policy
              </Text.Link>
            </Text.BodyXS>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

const googleTexts = [
  "Uses Gmail's labeling system to sort incoming emails and archives the non-essential",
  'Suggests meeting times from your Google Calendar',
  'Takes meeting notes and drafts follow ups',
  'Researches meeting attendees and companies',
  'Delivers a daily briefing via podcast',
  'Action items (coming soon)',
];
const microsoftTexts = [
  'Uses Outlook folders to sort incoming emails and archives the non-essential',
  'Suggests meeting times from your Outlook Calendar',
  'Takes meeting notes and drafts follow ups',
  'Researches meeting attendees and companies',
  'Delivers a daily briefing via podcast',
  'Action items (coming soon)',
];

type IntegrationItemProps = {
  service: 'Google' | 'Outlook';
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  isHovered?: boolean;
  onHover?: (hover: boolean) => void;
  animationDelay?: string;
};
function IntegrationItem({
  service,
  onClick,
  isHovered,
  onHover,
  animationDelay = '0s',
}: IntegrationItemProps) {
  const items = service === 'Google' ? googleTexts : microsoftTexts;
  const [itemsVisible, setItemsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setItemsVisible(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={css`
        border-radius: 24px;
        border: 1px solid #29282f;
        background: #161617;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        padding: 32px;
        animation: ${fadeInUp} 0.8s ease-out ${animationDelay} both;
        transition: all 0.3s ease;
        transform: ${isHovered ? 'translateY(-8px)' : 'translateY(0)'};
        box-shadow: ${isHovered
          ? '0 20px 40px rgba(255, 255, 255, 0.05), 0 0 20px rgba(255, 255, 255, 0.02)'
          : '0 4px 6px rgba(0, 0, 0, 0.1)'};
        position: relative;
        overflow: hidden;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.03),
            transparent
          );
          transition: left 0.5s ease;
        }

        &:hover::before {
          left: 100%;
        }

        @media (max-width: 648px) {
          margin-bottom: 24px;
        }
      `}
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
    >
      <div
        className={css`
          width: 100%;
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 60px;
          @media (max-width: 648px) {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        `}
      >
        <div
          className={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            width: 270px;
            justify-content: space-between;
            @media (max-width: 648px) {
              width: 100%;
            }
            @media (max-width: 810px) {
              flex-direction: column;
            }
          `}
        >
          <div
            className={css`
              @media (max-width: 648px) {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-start;
                width: 100%;
              }
            `}
          >
            {service === 'Google' ? (
              <CustomGoogleIcon
                style={{ width: '31px', height: '32px' }}
                className={css`
                  margin-bottom: 16px;
                  animation: ${isHovered ? pulse : 'none'} 1s ease-in-out
                    infinite;
                  transition: transform 0.3s ease;
                  transform: ${isHovered ? 'scale(1.1)' : 'scale(1)'};
                  @media (max-width: 648px) {
                    margin-right: 16px;
                    margin-bottom: 0px;
                  }
                `}
              />
            ) : (
              <MicrosoftIcon
                style={{ width: '32px', height: '33px' }}
                className={css`
                  margin-bottom: 16px;
                  animation: ${isHovered ? pulse : 'none'} 1s ease-in-out
                    infinite;
                  transition: transform 0.3s ease;
                  transform: ${isHovered ? 'scale(1.1)' : 'scale(1)'};
                  @media (max-width: 648px) {
                    margin-right: 16px;
                    margin-bottom: 0px;
                  }
                `}
              />
            )}
            <Text.H3
              className={css`
                transition: color 0.3s ease;
                color: ${isHovered ? '#ffffff' : 'inherit'};
              `}
            >
              {service === 'Google' ? 'Google' : 'Microsoft'} Workspace{' '}
            </Text.H3>
          </div>

          <div
            style={{ width: '100%' }}
            className={css`
              width: 100%;
              @media (max-width: 648px) {
                display: none;
              }
              & button {
                position: relative;
                overflow: hidden;
                transition: all 0.3s ease;

                &::after {
                  content: '';
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  width: 0;
                  height: 0;
                  border-radius: 50%;
                  background: rgba(255, 255, 255, 0.1);
                  transform: translate(-50%, -50%);
                  transition:
                    width 0.6s,
                    height 0.6s;
                }

                &:hover::after {
                  width: 300px;
                  height: 300px;
                }

                &:hover {
                  transform: translateY(-2px);
                  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
                }
              }
            `}
          >
            {service === 'Google' ? (
              <NewGoogleButton
                text="Continue with Gmail"
                onClick={onClick}
                width="90%"
              />
            ) : (
              <MicrosoftButton
                text="Continue with Outlook"
                onClick={onClick}
                disabled={true}
                width="90%"
              />
            )}
          </div>
        </div>

        <div
          className={css`
            display: flex;
            flex-direction: column;
            width: 100%;
          `}
        >
          {items.map((text, index) => (
            <div
              key={index}
              className={css`
                width: 100%;
                padding: 12px 0;
                border-bottom: 1px solid #212121;
                border-bottom-width: ${index === items.length - 1
                  ? '0'
                  : '1px'};
                opacity: ${itemsVisible ? '1' : '0'};
                transform: ${itemsVisible
                  ? 'translateX(0)'
                  : 'translateX(-20px)'};
                transition: all 0.4s ease-out;
                transition-delay: ${itemsVisible ? `0.2s` : '0s'};
                position: relative;
              `}
            >
              <Text.BodySM
                color="secondary"
                style={{
                  fontSize: '15px',
                  transition: 'color 0.3s ease',
                }}
                className={css`
                  ${isHovered ? 'color: #ffffff;' : ''}
                `}
              >
                {text}
              </Text.BodySM>
            </div>
          ))}
        </div>

        <div
          className={css`
            display: none;
            @media (max-width: 648px) {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
            }
            & button {
              position: relative;
              overflow: hidden;
              transition: all 0.3s ease;

              &::after {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                width: 0;
                height: 0;
                border-radius: 50%;
                background: rgba(255, 255, 255, 0.1);
                transform: translate(-50%, -50%);
                transition:
                  width 0.6s,
                  height 0.6s;
              }

              &:hover::after {
                width: 300px;
                height: 300px;
              }

              &:hover {
                transform: translateY(-2px);
                box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
              }
            }
          `}
        >
          {service === 'Google' ? (
            <NewGoogleButton
              text="Continue with Gmail"
              onClick={onClick}
              width="100%"
            />
          ) : (
            <MicrosoftButton
              text="Continue with Outlook"
              onClick={onClick}
              disabled={true}
              width="100%"
            />
          )}
        </div>
      </div>
    </div>
  );
}
